import { Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import BoltIcon from '@mui/icons-material/Bolt';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import CachedOutlinedIcon from '@mui/icons-material/CachedOutlined';
import React from 'react';
import { useAppSelector } from '../../app/hooks';
import {
  selectIsExpressUpsellMode,
  selectIsExpressTimerActive,
} from '../../selectors/menu';
import { selectFinalOrderSubmitted } from '../../redux/features/sessionBoundary/sessionBoundary.selectors';
import { selectIsCarPresent } from '../../selectors/message';
import { selectFeatureFlagHitl3 } from '../../redux/features/config/config.selector';
import {
  selectAIReusedAnswers,
  selectAIExpressModeActive,
} from '../../redux/features/ai/ai.selector';
import { ReusedAnswers } from '../../redux/features/ai/ai.props';
import { useReminder } from '../../contexts/ReminderContext';
import Colors from '../../utils/color';

/**
 * PULSE SIGNALS (HITL 3.0) — a chip row of live order signals in the Pulse strip.
 *
 * Chip kinds, each shown only when active (no heading, no empty-state — an empty
 * row simply renders nothing so it takes no space):
 *   • Express mode (amber) — express mode ON by either trigger
 *     (`isExpressUpsellMode || isExpressTimerActive`), dropped on car exit.
 *   • AI - Express mode (amber) — the AI reports express mode active via
 *     `ai_session_state` (`express_mode.active`). Distinct from the
 *     selector-driven Express chip above.
 *   • Reminder (violet) — the live slash-command reminder (`useReminder`), folded
 *     in from the old Action Center. A deliberately distinct colour from the
 *     AI-suggestion box so it doesn't read as the ask.
 *   • Reused answers (teal) — the reused-answers payload from `ai_session_state`
 *     (HITL 3.0), formatted as "Reuse: Drink size - Large, …" for the answer
 *     slots the AI carried over. Shown only when something was reused.
 *
 * (Sentiment is NOT a chip — it's a fixed vitals-row tile, see SentimentTile.)
 *
 * More signals (intent, upsell state, …) can slot into the same row later.
 * Gated by hitl_3_0 at the call site (CustomerPulse); the ai_session_state chips
 * also self-gate on the flag.
 */

// Capitalize the first letter only (sentence case): "large" -> "Large".
const sentenceCase = (value: string): string =>
  value ? value.charAt(0).toUpperCase() + value.slice(1) : value;

// Answer-slot key -> human label: "drink_size" -> "Drink size".
const humanizeKey = (key: string): string =>
  sentenceCase(key.replace(/_/g, ' '));

/**
 * Formats the ai_session_state `reused_answers` payload into the Pulse chip text.
 * Only entries flagged `reused: true` are shown; a slot with several reused
 * values joins them with " / ". Returns '' when there is nothing reused (empty
 * payload, all `reused: false`, or malformed) so the chip stays hidden.
 *
 *   { drink_size: [{ value: 'large', reused: true }] }
 *     -> "Reuse: Drink size - Large"
 *   { drink_size: [{ value: 'large', reused: true }],
 *     combo_size: [{ value: 'small', reused: true }] }
 *     -> "Reuse: Drink size - Large, Combo size - Small"
 */
export const formatReusedAnswers = (
  reusedAnswers: ReusedAnswers | null
): string => {
  if (!reusedAnswers || typeof reusedAnswers !== 'object') {
    return '';
  }
  const parts: string[] = [];
  Object.entries(reusedAnswers).forEach(([key, entries]) => {
    if (!Array.isArray(entries)) {
      return;
    }
    const values = entries
      .filter((e) => e && e.reused === true && typeof e.value === 'string')
      .map((e) => sentenceCase(e.value));
    if (values.length) {
      parts.push(`${humanizeKey(key)} - ${values.join(' / ')}`);
    }
  });
  return parts.length ? `Reuse: ${parts.join(', ')}` : '';
};
const useStyles = makeStyles((theme: Theme) => ({
  row: {
    marginTop: theme.spacing(1.25),
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'flex-start',
    gap: theme.spacing(0.75),
  },
  // Express signal — amber pill.
  sigchip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    padding: theme.spacing(0.375, 1, 0.375, 0.875),
    borderRadius: 999,
    backgroundColor: '#fff7e0',
    border: '1px solid #ffe19a',
    color: '#8a5d00',
    fontSize: '0.68rem',
    fontWeight: 600,
  },
  sigIcon: {
    fontSize: '0.85rem',
    color: Colors.amber,
    flexShrink: 0,
  },
  // Reminder — violet chip, distinct from both the amber signal and the
  // (blue-lavender) AI-suggestion box. Wraps for long reminders.
  remindChip: {
    display: 'inline-flex',
    alignItems: 'flex-start',
    gap: theme.spacing(0.625),
    padding: theme.spacing(0.5, 1.125, 0.5, 0.875),
    borderRadius: 12,
    backgroundColor: '#f0e9fb',
    border: '1px solid #d6c4ef',
    color: '#6a34ac',
    fontSize: '0.71rem',
    fontWeight: 500,
    lineHeight: 1.3,
    maxWidth: '100%',
    overflowWrap: 'anywhere',
  },
  remindIcon: {
    fontSize: '0.85rem',
    color: '#7c4dc4',
    flexShrink: 0,
    marginTop: 1,
  },
  // Reused answers — teal chip. Wraps like the reminder chip since the summary
  // can run long.
  reusedChip: {
    display: 'inline-flex',
    alignItems: 'flex-start',
    gap: theme.spacing(0.625),
    padding: theme.spacing(0.5, 1.125, 0.5, 0.875),
    borderRadius: 12,
    backgroundColor: '#e6f7f2',
    border: '1px solid #b8e6d8',
    color: '#0f7a5a',
    fontSize: '0.71rem',
    fontWeight: 500,
    lineHeight: 1.3,
    maxWidth: '100%',
    overflowWrap: 'anywhere',
  },
  reusedIcon: {
    fontSize: '0.85rem',
    color: '#12987a',
    flexShrink: 0,
    marginTop: 1,
  },
}));

const PulseSignals = () => {
  const classes = useStyles();
  const isHitl3 = useAppSelector(selectFeatureFlagHitl3);
  const isExpressUpsellMode = useAppSelector(selectIsExpressUpsellMode);
  const isExpressTimerActive = useAppSelector(selectIsExpressTimerActive);
  const isCarPresent = useAppSelector(selectIsCarPresent);
  const finalOrderSubmitted = useAppSelector(selectFinalOrderSubmitted);
  const reusedAnswers = useAppSelector(selectAIReusedAnswers);
  const aiExpressModeActive = useAppSelector(selectAIExpressModeActive);
  const { reminderMessage, isVisible: reminderVisible } = useReminder();

  // The Express chip is a per-order signal: only show it while a car is present
  // and the order is still live. Hide it once the car has left or the order is
  // finished/cancelled, so it never lingers past the interaction.
  const expressActive =
    (isExpressUpsellMode || isExpressTimerActive) &&
    isCarPresent &&
    !finalOrderSubmitted;
  const showReminder = reminderVisible && !!reminderMessage;

  // ai_session_state chips (HITL 3.0) — self-gated on the flag. Reused answers
  // shows when the formatted summary is non-empty; AI express mode when the AI
  // reports it active. (Sentiment is a tile, not a chip — see SentimentTile.)
  const reusedSummary = formatReusedAnswers(reusedAnswers);
  const showReusedAnswers = isHitl3 && !!reusedSummary;
  const showAIExpressMode = isHitl3 && aiExpressModeActive;

  // Empty row takes no space (no heading / placeholder).
  if (
    !expressActive &&
    !showAIExpressMode &&
    !showReminder &&
    !showReusedAnswers
  ) {
    return null;
  }

  return (
    <div className={classes.row} data-testid="pulse-signals">
      {expressActive && (
        <span className={classes.sigchip} data-testid="signal-express-mode">
          <BoltIcon className={classes.sigIcon} />
          Express
        </span>
      )}
      {showAIExpressMode && (
        <span className={classes.sigchip} data-testid="signal-ai-express-mode">
          <BoltIcon className={classes.sigIcon} />
          AI - Express mode
        </span>
      )}
      {showReusedAnswers && (
        <span
          className={classes.reusedChip}
          data-testid="signal-reused-answers"
        >
          <CachedOutlinedIcon className={classes.reusedIcon} />
          <span>{reusedSummary}</span>
        </span>
      )}
      {showReminder && (
        <span className={classes.remindChip} data-testid="signal-reminder">
          <NotificationsNoneOutlinedIcon className={classes.remindIcon} />
          <span>{reminderMessage}</span>
        </span>
      )}
    </div>
  );
};

export default PulseSignals;
